import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActiveFeaturesConfigcatService } from '~shared/modules/active-features/services';
import { Params, Router } from '@angular/router';
import { UrlUtils } from '~app/modules/generic/utils/url.utils';
import { MediaObserver } from '@angular/flex-layout';
import { MEDIA_ALISES } from '~shared/constants/media.constant';
import { select, Store } from '@ngrx/store';
import { getQueryParams } from '~core/store';
import { shareReplay, takeUntil } from 'rxjs/operators';
import { combineLatest, Observable, Subject, Subscription } from 'rxjs';
import {
  CatalogState,
  getProducts,
  getProductsTotalCount,
  getRecommendedProducts,
  getRecommendedProductsType,
  getSearchQuery,
} from '~app/modules/catalog/store';
import * as fromFilters from '../../filters/store';
import { NUMBER_1 } from '~shared/constants';
import { ProductsLoadingService } from '~app/modules/catalog/services/products-loading.service';
import { Product } from '~shared/models';
import { RecommendedProductsType } from '~app/modules/catalog/models';
import { CommonProps } from '../models';

@Component({
  selector: 'app-catalog-factory',
  templateUrl: './catalog-factory.component.html',
})
export class CatalogFactoryComponent implements OnInit, OnDestroy {
  private unsubscribe$ = new Subject();

  commonProps = {} as CommonProps;

  isCustomPlpEnabled$: Observable<boolean> = this.featureFlagService.isStoryblokCustomPLPEnabled();
  isCustomPlpRoute: boolean = UrlUtils.isCustomPLPPage(this.router.url);
  isCategoryPlpRoute: boolean = UrlUtils.isCategoryPage(this.router.url);
  catalogLoadingSubscription: Subscription;

  constructor(
    private featureFlagService: ActiveFeaturesConfigcatService,
    private productsLoadingService: ProductsLoadingService,
    private filtersStore: Store<fromFilters.State>,
    private mediaObserver: MediaObserver,
    private store$: Store<CatalogState>,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.initializeSearchQuery();
    this.loadQueryParams();
    this.initializeRecommendedProducts();
    this.initializeFilters();
    this.initializeMediaObservable();
    this.initializeProducts();
    this.initializeSkeletonLoading();
  }

  ngOnDestroy(): void {
    this.catalogLoadingSubscription.unsubscribe();

    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  private initializeSearchQuery(): void {
    this.store$.pipe(select(getSearchQuery), takeUntil(this.unsubscribe$)).subscribe((searchQuery: string) => {
      this.commonProps.searchQuery = searchQuery;
    });
  }

  private initializeProducts(): void {
    combineLatest([
      this.store$.pipe(select(getProducts), takeUntil(this.unsubscribe$)),
      this.productsLoadingService.isProductsLoading$,
      this.store$.pipe(select(getProductsTotalCount), takeUntil(this.unsubscribe$)),
    ]).subscribe(([products, isLoadingProducts, productsCount]) => {
      this.commonProps.products = products;
      this.commonProps.isZeroResult = !isLoadingProducts && products?.length < NUMBER_1;
      this.commonProps.productsTotalCount = productsCount;
    });
  }

  private initializeRecommendedProducts(): void {
    this.store$
      .pipe(select(getRecommendedProducts), takeUntil(this.unsubscribe$))
      .subscribe((recommendedProducts: Product[]) => {
        this.commonProps.recommendedProducts = recommendedProducts;
      });
    this.store$
      .pipe(select(getRecommendedProductsType), takeUntil(this.unsubscribe$))
      .subscribe((recommendedProductsType: RecommendedProductsType) => {
        this.commonProps.recommendedProductsType = recommendedProductsType;
      });
  }

  private initializeSkeletonLoading(): void {
    this.catalogLoadingSubscription = this.productsLoadingService.isCatalogLoading$.subscribe(
      (isCatelogLoading: boolean) => {
        this.commonProps.isLoading = isCatelogLoading;
      }
    );
  }

  private loadQueryParams(): void {
    this.store$
      .pipe(select(getQueryParams, takeUntil(this.unsubscribe$)), shareReplay(1))
      .subscribe((queryParams: Params) => {
        this.commonProps.queryParams = queryParams;
      });
  }

  private initializeFilters(): void {
    this.filtersStore.pipe(select(getQueryParams), takeUntil(this.unsubscribe$)).subscribe((queryParams: Params) => {
      const filters = queryParams.filter || {};
      this.commonProps.hasFilters = !!Object.keys(filters)?.length;
      /*this.hasFilters = !!(filters.options || filters.price || filters.promotion || filters.categories || filters.volumePrices);*/
      const hasPromoTag: boolean = !!(filters.promoTag || false);
      this.commonProps.isExpired = this.commonProps.hasFilters && hasPromoTag;
    });
  }

  private initializeMediaObservable(): void {
    this.mediaObserver
      .asObservable()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => {
        this.commonProps.isMobileOrTablet = this.mediaObserver.isActive(MEDIA_ALISES.mobile)
          || this.mediaObserver.isActive(MEDIA_ALISES.tablet);
        this.commonProps.isMobile = this.mediaObserver.isActive(MEDIA_ALISES.mobile);
        this.commonProps.isTablet = this.mediaObserver.isActive(MEDIA_ALISES.tablet);
        this.commonProps.isDesktop = this.mediaObserver.isActive(MEDIA_ALISES.desktop);
        this.commonProps.isLandScapeTablet = this.mediaObserver.isActive(MEDIA_ALISES.tabletLandscape);
      });
  }
}
